import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import {
  ConversationId as ConversationIdSchema,
  DraftMessageId as DraftMessageIdSchema,
  MessageId as MessageIdSchema,
} from "@redotech/effect-api-shared/ids/definitions";
import {
  DraftResponse,
  type SaveDraftInput,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/draft";
import { type MessageWithConversationId } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import { ConversationThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import { ConversationTimelineEventResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/timeline-event/timeline-event-response";
import {
  TagGroupResponse,
  TagResponse,
} from "@redotech/effect-api-shared/rpc/tag";
import type { RedoMerchantRpcClient } from "@redotech/merchant-sdk/rpc/client";
import type { RedoMerchantClient } from "@redotech/redo-merchant-app-common/client";
import { getTeamReturns } from "@redotech/redo-merchant-app-common/client/team";
import type { EffectApiContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { FulfillmentGroupPickStatus } from "@redotech/redo-model/fulfillments/fulfillment-group";
import { Order } from "@redotech/redo-model/order";
import type { MerchantAppReturn } from "@redotech/redo-model/return";
import { parseLoadSubsetOptions } from "@tanstack/db";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { createCollection } from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import { Option, Schema } from "effect";
import { createConversationsListCollection } from "./conversation-list-collection";
import { catchApiErrors } from "./error-handling";

export const conversationQueryKeys: {
  readonly all: readonly ["conversations"];
  readonly threads: () => readonly ["conversations", "threads"];
  readonly messages: () => readonly ["conversations", "messages"];
  readonly timelineEvents: () => readonly ["conversations", "timelineEvents"];
} = {
  all: ["conversations"] as const,
  threads: () => [...conversationQueryKeys.all, "threads"] as const,
  messages: () => [...conversationQueryKeys.all, "messages"] as const,
  timelineEvents: () =>
    [...conversationQueryKeys.all, "timelineEvents"] as const,
};

export const tagQueryKeys: { readonly all: readonly ["tags"] } = {
  all: ["tags"] as const,
};

export const presenceQueryKeys: { readonly all: readonly ["presence"] } = {
  all: ["presence"] as const,
};

export interface ConversationPresenceRecord {
  // Composite key: `${conversationId}:${userId}`
  id: string;
  conversationId: ConversationId;
  userId: string;
  isViewing: boolean;
  isTyping: boolean;
}

const ConversationPresenceRecordSchema = Schema.Struct({
  id: Schema.String,
  conversationId: ConversationIdSchema,
  userId: Schema.String,
  isViewing: Schema.Boolean,
  isTyping: Schema.Boolean,
});

export const orderQueryKeys: {
  readonly all: readonly ["orders"];
  readonly byEmail: (email: string) => readonly ["orders", "byEmail", string];
} = {
  all: ["orders"] as const,
  byEmail: (email: string) =>
    [...orderQueryKeys.all, "byEmail", email] as const,
};

export const returnQueryKeys: {
  readonly all: readonly ["returns"];
  readonly byEmail: (email: string) => readonly ["returns", "byEmail", string];
} = {
  all: ["returns"] as const,
  byEmail: (email: string) =>
    [...returnQueryKeys.all, "byEmail", email] as const,
};

export const draftQueryKeys: { readonly all: readonly ["drafts"] } = {
  all: ["drafts"] as const,
};

export type DraftCollectionItem = Omit<DraftResponse, "inReplyToMessageId"> & {
  inReplyToMessageId: string | undefined;
};

interface CreateGlobalCollectionsOptions {
  queryClient: QueryClient;
  effectApi: EffectApiContext;
  rpcClient: RedoMerchantRpcClient;
  merchantClient: RedoMerchantClient;
}

const FIVE_MINUTES_MS = 5 * 60 * 1000;

export async function fetchConversationThreads(
  effectApi: EffectApiContext,
  conversationId: ConversationId,
): Promise<ConversationThread[]> {
  const client = effectApi.getClient();
  const result = await effectApi.runPromise(
    catchApiErrors(
      client.conversation.getConversationThreads({
        path: { id: ConversationIdSchema.make(conversationId) },
        urlParams: { limit: 200 },
      }),
    ),
  );
  return [...result.items];
}

export async function fetchConversationMessages(
  effectApi: EffectApiContext,
  conversationId: ConversationId,
): Promise<MessageWithConversationId[]> {
  const client = effectApi.getClient();
  const result = await effectApi.runPromise(
    catchApiErrors(
      client.conversation.getConversationMessages({
        path: { id: ConversationIdSchema.make(conversationId) },
        urlParams: { limit: 200 },
      }),
    ),
  );
  return result.items.map((message) => ({
    ...message,
    conversationId: ConversationIdSchema.make(conversationId),
  }));
}

function createThreadsCollection({
  queryClient,
  effectApi,
}: CreateGlobalCollectionsOptions) {
  return createCollection(
    queryCollectionOptions({
      queryKey: conversationQueryKeys.threads(),
      queryFn: async (ctx): Promise<ConversationThread[]> => {
        const { filters } = parseLoadSubsetOptions(ctx.meta?.loadSubsetOptions);

        const conversationIdFilter = filters.find(
          (f) => f.field[0] === "conversationId",
        );

        if (
          conversationIdFilter?.operator !== "eq" ||
          typeof conversationIdFilter.value !== "string"
        ) {
          return [];
        }

        return fetchConversationThreads(
          effectApi,
          ConversationIdSchema.make(conversationIdFilter.value),
        );
      },
      queryClient,
      getKey: (item) => item.id.toString(),
      syncMode: "on-demand",
      schema: Schema.standardSchemaV1(ConversationThread),
    }),
  );
}

function createMessagesCollection({
  queryClient,
  effectApi,
}: CreateGlobalCollectionsOptions) {
  return createCollection<MessageWithConversationId, string>(
    queryCollectionOptions({
      queryKey: conversationQueryKeys.messages(),
      queryFn: async (ctx): Promise<MessageWithConversationId[]> => {
        const { filters } = parseLoadSubsetOptions(ctx.meta?.loadSubsetOptions);

        const conversationIdFilter = filters.find(
          (f) => f.field[0] === "conversationId",
        );

        if (
          conversationIdFilter?.operator !== "eq" ||
          typeof conversationIdFilter.value !== "string"
        ) {
          return [];
        }

        return fetchConversationMessages(
          effectApi,
          ConversationIdSchema.make(conversationIdFilter.value),
        );
      },
      queryClient,
      getKey: (item) => item.id.toString(),
      syncMode: "on-demand",
    }),
  );
}

function createTimelineEventsCollection({
  queryClient,
  effectApi,
}: CreateGlobalCollectionsOptions) {
  return createCollection(
    queryCollectionOptions({
      queryKey: conversationQueryKeys.timelineEvents(),
      schema: Schema.standardSchemaV1(ConversationTimelineEventResponse),
      queryFn: async (ctx): Promise<ConversationTimelineEventResponse[]> => {
        const { filters } = parseLoadSubsetOptions(ctx.meta?.loadSubsetOptions);

        const conversationIdFilter = filters.find(
          (f) => f.field[0] === "conversationId",
        );

        if (
          conversationIdFilter?.operator !== "eq" ||
          typeof conversationIdFilter.value !== "string"
        ) {
          return [];
        }

        const client = effectApi.getClient();
        const conversationId = ConversationIdSchema.make(
          conversationIdFilter.value,
        );
        const result = await effectApi.runPromise(
          catchApiErrors(
            client.conversation.getConversationTimelineEvents({
              path: { id: conversationId },
            }),
          ),
        );
        return [...result.items];
      },
      queryClient,
      getKey: (item) => item.id,
      syncMode: "on-demand",
    }),
  );
}

interface AccountTagsResponse {
  tags: TagResponse[];
  tagGroups: TagGroupResponse[];
}

async function fetchAccountTags(
  effectApi: EffectApiContext,
): Promise<AccountTagsResponse> {
  const client = effectApi.getClient();
  const result = await effectApi.runPromise(
    catchApiErrors(client.tag.getAccountTags({})),
  );
  return { tags: [...result.tags], tagGroups: [...result.tagGroups] };
}

function createPresenceCollection({
  queryClient,
}: CreateGlobalCollectionsOptions) {
  return createCollection(
    queryCollectionOptions({
      queryKey: presenceQueryKeys.all,
      queryFn: async (): Promise<ConversationPresenceRecord[]> => [],
      getKey: (item) => item.id,
      queryClient,
      syncMode: "on-demand",
      staleTime: Infinity,
      schema: Schema.standardSchemaV1(ConversationPresenceRecordSchema),
    }),
  );
}

function createTagsCollection({
  queryClient,
  effectApi,
}: CreateGlobalCollectionsOptions) {
  return createCollection(
    queryCollectionOptions({
      queryKey: tagQueryKeys.all,
      queryFn: async () => fetchAccountTags(effectApi),
      select: (data: AccountTagsResponse) => data.tags,
      getKey: (item) => item.id,
      queryClient,
      staleTime: FIVE_MINUTES_MS,
      schema: Schema.standardSchemaV1(TagResponse),
    }),
  );
}

function createTagGroupsCollection({
  queryClient,
  effectApi,
}: CreateGlobalCollectionsOptions) {
  return createCollection(
    queryCollectionOptions({
      queryKey: tagQueryKeys.all,
      queryFn: async () => fetchAccountTags(effectApi),
      select: (data: AccountTagsResponse) => data.tagGroups,
      getKey: (item) => item.id,
      queryClient,
      staleTime: FIVE_MINUTES_MS,
      schema: Schema.standardSchemaV1(TagGroupResponse),
    }),
  );
}

export type OrderWithPickStatus = Order & {
  pickStatuses: FulfillmentGroupPickStatus[];
};

function createOrdersCollection({
  queryClient,
  rpcClient,
}: CreateGlobalCollectionsOptions) {
  return createCollection<OrderWithPickStatus, string>(
    queryCollectionOptions({
      queryKey: orderQueryKeys.all,
      queryFn: async (ctx): Promise<OrderWithPickStatus[]> => {
        const { filters } = parseLoadSubsetOptions(ctx.meta?.loadSubsetOptions);

        const emailFilter = filters.find((f) => f.field[0] === "shopify");

        if (
          emailFilter?.operator !== "eq" ||
          typeof emailFilter.value !== "string"
        ) {
          return [];
        }

        const result = await rpcClient.getTeamOrdersByEmail({
          email: emailFilter.value,
        });

        return result.orders as OrderWithPickStatus[];
      },
      queryClient,
      getKey: (item) => item._id,
      syncMode: "on-demand",
      staleTime: FIVE_MINUTES_MS,
    }),
  );
}

async function fetchReturnsByEmail(
  merchantClient: RedoMerchantClient,
  email: string,
): Promise<MerchantAppReturn[]> {
  const result = await getTeamReturns(merchantClient, { email });
  return result as MerchantAppReturn[];
}

function createReturnsCollection({
  queryClient,
  merchantClient,
}: CreateGlobalCollectionsOptions) {
  return createCollection<MerchantAppReturn, string>(
    queryCollectionOptions({
      queryKey: returnQueryKeys.all,
      queryFn: async (ctx): Promise<MerchantAppReturn[]> => {
        const { filters } = parseLoadSubsetOptions(ctx.meta?.loadSubsetOptions);

        const emailFilter = filters.find(
          (f) => f.field[0] === "shipping_address",
        );

        if (
          emailFilter?.operator !== "eq" ||
          typeof emailFilter.value !== "string"
        ) {
          return [];
        }

        return fetchReturnsByEmail(merchantClient, emailFilter.value);
      },
      queryClient,
      getKey: (item) => item._id,
      syncMode: "on-demand",
      staleTime: FIVE_MINUTES_MS,
    }),
  );
}

function buildSaveDraftPayload(item: DraftCollectionItem): SaveDraftInput {
  const base = {
    threadId: item.threadId,
    inReplyToMessageId: item.inReplyToMessageId
      ? MessageIdSchema.make(item.inReplyToMessageId)
      : undefined,
    content: item.content,
    plainContent: item.plainContent,
    attachments: [...item.attachments],
  };

  if (item.channel === "gmail" || item.channel === "outlook") {
    return {
      ...base,
      channel: item.channel,
      channelData: item.channelData ?? {},
    } as SaveDraftInput;
  }
  return {
    ...base,
    channel: item.channel,
    channelData: null,
  } as SaveDraftInput;
}

function createDraftsCollection({
  queryClient,
  effectApi,
}: CreateGlobalCollectionsOptions) {
  return createCollection<DraftCollectionItem, string>(
    queryCollectionOptions({
      queryKey: draftQueryKeys.all,
      queryFn: async (ctx): Promise<DraftCollectionItem[]> => {
        const { filters } = parseLoadSubsetOptions(ctx.meta?.loadSubsetOptions);
        const conversationIdFilter = filters.find(
          (f) => f.field[0] === "conversationId",
        );
        if (
          conversationIdFilter?.operator !== "eq" ||
          typeof conversationIdFilter.value !== "string"
        ) {
          throw new Error(
            "Drafts collection requires a single conversationId filter",
          );
        }
        const client = effectApi.getClient();
        const conversationId = ConversationIdSchema.make(
          conversationIdFilter.value,
        );
        const result = await effectApi.runPromise(
          catchApiErrors(
            client.conversation.getConversationDrafts({
              path: { id: conversationId },
            }),
          ),
        );
        return result.drafts.map((d) => ({
          ...d,
          inReplyToMessageId: Option.getOrUndefined(
            d.inReplyToMessageId,
          )?.toString(),
        }));
      },
      queryClient,
      getKey: (item) => item.id.toString(),
      syncMode: "on-demand",
      onInsert: async ({ transaction }) => {
        const client = effectApi.getClient();
        for (const mutation of transaction.mutations) {
          const item = mutation.modified;
          const payload = buildSaveDraftPayload(item);
          await effectApi.runPromise(
            catchApiErrors(
              client.conversation.saveDraft({
                path: { id: ConversationIdSchema.make(item.conversationId) },
                payload,
              } as Parameters<typeof client.conversation.saveDraft>[0]),
            ),
          );
        }
        await transaction.mutations[0].collection.utils.refetch();
      },
      onUpdate: async ({ transaction }) => {
        const client = effectApi.getClient();
        for (const mutation of transaction.mutations) {
          const item = mutation.modified;
          const payload = buildSaveDraftPayload(item);
          await effectApi.runPromise(
            catchApiErrors(
              client.conversation.saveDraft({
                path: { id: ConversationIdSchema.make(item.conversationId) },
                payload,
              } as Parameters<typeof client.conversation.saveDraft>[0]),
            ),
          );
        }
        return { refetch: false };
      },
      onDelete: async ({ transaction }) => {
        const client = effectApi.getClient();
        for (const mutation of transaction.mutations) {
          const item = mutation.original as DraftCollectionItem;
          await effectApi.runPromise(
            catchApiErrors(
              client.conversation.deleteDraft({
                path: {
                  id: ConversationIdSchema.make(item.conversationId),
                  draftId: DraftMessageIdSchema.make(String(mutation.key)),
                },
              }),
            ),
          );
        }
      },
    }),
  );
}

export type ConversationsListCollection = ReturnType<
  typeof createConversationsListCollection
>;
export type ThreadsCollection = ReturnType<typeof createThreadsCollection>;
export type MessagesCollection = ReturnType<typeof createMessagesCollection>;
export type DraftsCollection = ReturnType<typeof createDraftsCollection>;

export type PresenceCollection = ReturnType<typeof createPresenceCollection>;
export type TagsCollection = ReturnType<typeof createTagsCollection>;
export type TagGroupsCollection = ReturnType<typeof createTagGroupsCollection>;
export type OrdersCollection = ReturnType<typeof createOrdersCollection>;
export type ReturnsCollection = ReturnType<typeof createReturnsCollection>;
export type TimelineEventsCollection = ReturnType<
  typeof createTimelineEventsCollection
>;

export interface GlobalConversationCollections {
  conversationsList: ConversationsListCollection;
  threads: ThreadsCollection;
  messages: MessagesCollection;
  drafts: DraftsCollection;
  presence: PresenceCollection;
  tags: TagsCollection;
  tagGroups: TagGroupsCollection;
  orders: OrdersCollection;
  returns: ReturnsCollection;
  timelineEvents: TimelineEventsCollection;
}

export function createGlobalConversationCollections(
  options: CreateGlobalCollectionsOptions,
): GlobalConversationCollections {
  const conversationsList = createConversationsListCollection(options);
  const threads = createThreadsCollection(options);
  const messages = createMessagesCollection(options);
  const drafts = createDraftsCollection(options);
  const presence = createPresenceCollection(options);
  const tags = createTagsCollection(options);
  const tagGroups = createTagGroupsCollection(options);
  const orders = createOrdersCollection(options);
  const returns = createReturnsCollection(options);
  const timelineEvents = createTimelineEventsCollection(options);
  return {
    conversationsList,
    threads,
    messages,
    drafts,
    presence,
    tags,
    tagGroups,
    orders,
    returns,
    timelineEvents,
  };
}
